import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Check, CircleHelp, RotateCcw, ThumbsUp, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CaptureStatus, ConfirmCaptureResponse, ReasonCode, VerdictRequest } from '@/lib/api/types';
import { REASON_WORDS, humanise } from './review-model';

/** What the last action on this screen came to, in the operator's words. */
export type FooterOutcome =
  | { kind: 'confirmed'; response: ConfirmCaptureResponse }
  | { kind: 'escalated' }
  | { kind: 'verdict'; verdict: VerdictRequest['verdict']; status: CaptureStatus };

interface OperatorFooterProps {
  pendingChanges: number;
  busy: boolean;
  canConfirm: boolean;
  canEscalate: boolean;
  onConfirm: () => void;
  onEscalate: (question: string) => Promise<boolean>;
}

/**
 * The operator's footer (ADR-006 decision 3). "Done, next item" sends every
 * edit, add and reason on the screen in one confirm. The answer says whether
 * the capture was approved on the spot or went to the manager, and why.
 * "Send to manager with a question" asks for the question first, because a
 * manager handed a capture with no question has nothing to answer.
 */
export function OperatorFooter({ pendingChanges, busy, canConfirm, canEscalate, onConfirm, onEscalate }: OperatorFooterProps) {
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');

  async function send() {
    const sent = await onEscalate(question.trim());
    if (sent) {
      setAsking(false);
      setQuestion('');
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {asking ? (
        <div className="flex flex-col gap-2 rounded-card border border-border p-3">
          <label htmlFor="escalate-question" className="text-sm">
            What should your manager look at?
          </label>
          <textarea
            id="escalate-question"
            rows={3}
            value={question}
            disabled={busy}
            onChange={(event) => setQuestion(event.target.value)}
            className="w-full rounded-control border border-border bg-transparent px-3 py-2 text-sm text-surface-foreground placeholder:text-muted focus-visible:border-brand focus-visible:outline-none"
            placeholder="For example: the plate says 6205 but the box says 6204, which is right?"
          />
          {pendingChanges > 0 && (
            <p className="text-xs text-muted">
              Your {pendingChanges === 1 ? 'change stays' : `${pendingChanges} changes stay`} on this screen. Sending a
              question does not save {pendingChanges === 1 ? 'it' : 'them'}; &ldquo;Done, next item&rdquo; does.
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button type="button" disabled={busy || question.trim() === ''} onClick={() => void send()}>
              Send the question
            </Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => setAsking(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button type="button" size="lg" className="sm:flex-1" disabled={busy || !canConfirm} onClick={onConfirm}>
            <Check aria-hidden="true" />
            {busy ? 'Saving...' : 'Done, next item'}
          </Button>
          <Button
            type="button"
            size="lg"
            variant="outline"
            className="sm:flex-1"
            disabled={busy || !canEscalate}
            onClick={() => setAsking(true)}
          >
            <CircleHelp aria-hidden="true" />
            Send to manager with a question
          </Button>
        </div>
      )}
      {pendingChanges > 0 && !asking && (
        <p className="text-xs text-muted">
          {pendingChanges === 1 ? 'One change' : `${pendingChanges} changes`} will be saved with your confirmation.
        </p>
      )}
    </div>
  );
}

interface ReviewerFooterProps {
  busy: boolean;
  pendingChanges: number;
  canDecide: boolean;
  onVerdict: (verdict: 'approve' | 'reject' | 'request_recapture', reason?: string) => Promise<boolean>;
}

/**
 * The reviewer's footer (ADR-006 decision 3): approve, reject and request
 * re-capture through the section 12 verdict route. A reject or a re-capture
 * asks for the reason first, because the API refuses either without one
 * and the operator reads it.
 */
export function ReviewerFooter({ busy, pendingChanges, canDecide, onVerdict }: ReviewerFooterProps) {
  const [pending, setPending] = useState<'reject' | 'request_recapture' | null>(null);
  const [reason, setReason] = useState('');

  async function decide() {
    if (!pending) return;
    const done = await onVerdict(pending, reason.trim());
    if (done) {
      setPending(null);
      setReason('');
    }
  }

  if (pending) {
    return (
      <div className="flex flex-col gap-2 rounded-card border border-border p-3">
        <label htmlFor="verdict-reason" className="text-sm">
          {pending === 'reject' ? 'Why is this capture rejected?' : 'What should the operator photograph again?'}
        </label>
        <textarea
          id="verdict-reason"
          rows={3}
          value={reason}
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
          className="w-full rounded-control border border-border bg-transparent px-3 py-2 text-sm text-surface-foreground placeholder:text-muted focus-visible:border-brand focus-visible:outline-none"
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            variant={pending === 'reject' ? 'destructive' : 'primary'}
            disabled={busy || reason.trim() === ''}
            onClick={() => void decide()}
          >
            {pending === 'reject' ? 'Reject the capture' : 'Request the re-capture'}
          </Button>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => setPending(null)}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          type="button"
          size="lg"
          className="sm:flex-1"
          disabled={busy || !canDecide}
          onClick={() => void onVerdict('approve')}
        >
          <ThumbsUp aria-hidden="true" />
          {busy ? 'Saving...' : pendingChanges > 0 ? 'Save and approve' : 'Approve'}
        </Button>
        <Button
          type="button"
          size="lg"
          variant="outline"
          className="sm:flex-1"
          disabled={busy || !canDecide}
          onClick={() => setPending('request_recapture')}
        >
          <RotateCcw aria-hidden="true" />
          Request re-capture
        </Button>
        <Button
          type="button"
          size="lg"
          variant="destructive"
          className="sm:flex-1"
          disabled={busy || !canDecide}
          onClick={() => setPending('reject')}
        >
          <X aria-hidden="true" />
          Reject
        </Button>
      </div>
      {pendingChanges > 0 && (
        <p className="text-xs text-muted">
          {pendingChanges === 1 ? 'One change is' : `${pendingChanges} changes are`} saved before the approval.
        </p>
      )}
    </div>
  );
}

function reasonWords(code: ReasonCode | undefined): string {
  return code ? REASON_WORDS[code] : 'it needs a second look';
}

const VERDICT_WORDS: Record<VerdictRequest['verdict'], string> = {
  approve: 'Approved.',
  reject: 'Rejected. The operator sees your reason.',
  request_recapture: 'Re-capture requested. The operator finds it on their flagged list with your reason.',
  edit: 'Changes saved.',
};

/**
 * What the last action came to. After a confirm this is the answer the
 * operator waited for: approved on the spot, or with the manager and why,
 * with every field still outstanding named.
 */
export function OutcomePanel({ outcome }: { outcome: FooterOutcome }) {
  return (
    <ScrollIntoView key={JSON.stringify(outcome)}>
      <OutcomeBody outcome={outcome} />
    </ScrollIntoView>
  );
}

/* The answer lands above the footer the operator just tapped, which on a
   phone can sit off screen, so it brings itself into view once. */
function ScrollIntoView({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    ref.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, []);
  return <div ref={ref}>{children}</div>;
}

function OutcomeBody({ outcome }: { outcome: FooterOutcome }) {
  if (outcome.kind === 'escalated') {
    return (
      <div className="flex flex-col gap-2 rounded-card border border-border p-3 text-sm" role="status">
        <p>Sent to your manager with your question. You can keep working on this capture or move on.</p>
        <NextItemLink />
      </div>
    );
  }

  if (outcome.kind === 'verdict') {
    return (
      <div className="rounded-card border border-border p-3 text-sm" role="status">
        <p>{VERDICT_WORDS[outcome.verdict]}</p>
      </div>
    );
  }

  const { response } = outcome;
  const approved = response.status === 'APPROVED' || response.status === 'AUTO_APPROVED';
  const outstanding = response.outstanding ?? [];

  return (
    <div
      className={`flex flex-col gap-2 rounded-card border p-3 text-sm ${approved ? 'border-success' : 'border-warning'}`}
      role="status"
    >
      {approved ? (
        <p className="flex items-center gap-2 text-success">
          <Check className="size-4 shrink-0" aria-hidden="true" />
          Approved. Nothing more is needed on this item.
        </p>
      ) : response.status === undefined ? (
        <p className="flex items-center gap-2 text-success">
          <Check className="size-4 shrink-0" aria-hidden="true" />
          Confirmed. The reviewer sees that you checked this one.
        </p>
      ) : (
        <p className="text-warning">Sent to your manager, because {reasonWords(response.reasonCode)}.</p>
      )}
      {outstanding.length > 0 && (
        <p>Still needed: {outstanding.map((name) => humanise(name)).join(', ')}.</p>
      )}
      <NextItemLink />
    </div>
  );
}

function NextItemLink() {
  return (
    <Link
      to="/capture"
      className="inline-flex min-h-11 w-fit items-center rounded-control bg-brand px-5 text-sm font-medium text-brand-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      Capture the next item
    </Link>
  );
}
